import { fetchCommonData } from "@/services/CommonService";
import {
  COUNTRY_TO_LOCALE_MAP,
  KNOWN_COUNTRIES,
} from "@/Config/countryConstants";

// Cache for country validation to avoid repeated API calls
let countryValidationCache: { [key: string]: boolean } = {};
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Validates if a country exists in the backend by attempting to fetch common data
 * @param countryCode - The country code to validate (e.g., 'india', 'kenya', 'nigeria')
 * @returns Promise<boolean> - true if country exists, false otherwise
 */
export const validateCountryExists = async (
  countryCode: string
): Promise<boolean> => {
  // Check cache first
  const now = Date.now();
  if (
    now - cacheTimestamp < CACHE_DURATION &&
    countryValidationCache[countryCode] !== undefined
  ) {
    return countryValidationCache[countryCode];
  }

  try {
    // Map country code to locale using the constant from countryConstants
    const locale = COUNTRY_TO_LOCALE_MAP[countryCode.toLowerCase()] || "en";

    // Attempt to fetch common data for this country
    const response = await fetchCommonData(locale);

    // If we get data back, the country exists
    const exists = response?.data !== null && response?.data !== undefined;

    // Update cache
    countryValidationCache[countryCode] = exists;
    cacheTimestamp = now;

    return exists;
  } catch (error) {
    console.error(
      `CountryValidation: Error validating country ${countryCode}:`,
      error
    );

    // On error, assume country doesn't exist for safety
    countryValidationCache[countryCode] = false;
    cacheTimestamp = now;

    return false;
  }
};

/**
 * Gets list of valid countries from backend
 * @returns Promise<string[]> - Array of valid country codes
 */
export const getValidCountries = async (): Promise<string[]> => {
  const validCountries: string[] = [];

  for (const country of KNOWN_COUNTRIES) {
    const isValid = await validateCountryExists(country);
    if (isValid) {
      validCountries.push(country);
    }
  }

  return validCountries;
};

/**
 * Clears the country validation cache
 */
export const clearCountryValidationCache = (): void => {
  countryValidationCache = {};
  cacheTimestamp = 0;
};
